/**
 * Shared building materials and utilities
 */

import * as THREE from 'three';

export interface BuildingMaterialSet {
  stone: THREE.MeshStandardMaterial;
  darkWood: THREE.MeshStandardMaterial;
  wood: THREE.MeshStandardMaterial;
  roof: THREE.MeshStandardMaterial;
  roofEdge: THREE.MeshStandardMaterial;
  metal: THREE.MeshStandardMaterial;
  gold: THREE.MeshStandardMaterial;
  lantern: THREE.MeshStandardMaterial;
  glow: THREE.MeshStandardMaterial;
  flag: THREE.MeshStandardMaterial;
}

/**
 * Create a standard set of building materials
 */
export function createBuildingMaterials(
  mainColor: THREE.Color,
  accentColor: THREE.Color
): BuildingMaterialSet {
  return {
    stone: new THREE.MeshStandardMaterial({ color: 0x6b6b6b, roughness: 0.95 }),
    darkWood: new THREE.MeshStandardMaterial({ color: 0x3d2817, roughness: 0.85 }),
    wood: new THREE.MeshStandardMaterial({ color: 0x5c3a21, roughness: 0.8 }),
    roof: new THREE.MeshStandardMaterial({ color: 0x4a3728, roughness: 0.7 }),
    roofEdge: new THREE.MeshStandardMaterial({ color: 0x2d1f15, roughness: 0.6 }),
    metal: new THREE.MeshStandardMaterial({ color: 0xb0b0b0, roughness: 0.4, metalness: 0.6 }),
    gold: new THREE.MeshStandardMaterial({ color: 0xd4af37, roughness: 0.3, metalness: 0.6 }),
    lantern: new THREE.MeshStandardMaterial({
      color: 0xc62828,
      emissive: 0xff3d00,
      emissiveIntensity: 0.4
    }),
    glow: new THREE.MeshStandardMaterial({
      color: 0xffcc80,
      emissive: 0xff9800,
      emissiveIntensity: 0.6
    }),
    flag: new THREE.MeshStandardMaterial({
      color: 0xc62828,
      side: THREE.DoubleSide,
      roughness: 0.9
    }),
  };
}

/**
 * Create a red lantern mesh
 */
export function createLantern(
  group: THREE.Group,
  x: number,
  y: number,
  z: number,
  lanternMat: THREE.MeshStandardMaterial
): void {
  // Lantern body
  const lanternBodyGeom = new THREE.CylinderGeometry(0.1, 0.12, 0.22, 8);
  const lanternBody = new THREE.Mesh(lanternBodyGeom, lanternMat);
  lanternBody.position.set(x, y, z);
  group.add(lanternBody);

  // Lantern top cap
  const topCapGeom = new THREE.CylinderGeometry(0.06, 0.08, 0.05, 8);
  const topCap = new THREE.Mesh(topCapGeom, new THREE.MeshStandardMaterial({ color: 0x8b4513, roughness: 0.7 }));
  topCap.position.set(x, y + 0.12, z);
  group.add(topCap);

  // Lantern bottom cap
  const bottomCapGeom = new THREE.CylinderGeometry(0.08, 0.06, 0.05, 8);
  const bottomCap = new THREE.Mesh(bottomCapGeom, new THREE.MeshStandardMaterial({ color: 0x8b4513, roughness: 0.7 }));
  bottomCap.position.set(x, y - 0.12, z);
  group.add(bottomCap);

  // Lantern tassel
  const tasselGeom = new THREE.ConeGeometry(0.03, 0.1, 6);
  const tassel = new THREE.Mesh(tasselGeom, new THREE.MeshStandardMaterial({ color: 0xffcc00, roughness: 0.8 }));
  tassel.position.set(x, y - 0.2, z);
  tassel.rotation.x = Math.PI;
  group.add(tassel);

  // Hanging wire
  const wireGeom = new THREE.CylinderGeometry(0.01, 0.01, 0.2, 4);
  const wire = new THREE.Mesh(wireGeom, new THREE.MeshStandardMaterial({ color: 0x333333 }));
  wire.position.set(x, y + 0.22, z);
  group.add(wire);
}

/**
 * Create a flag with pole
 */
export function createFlag(
  group: THREE.Group,
  x: number,
  y: number,
  z: number,
  poleHeight: number,
  flagWidth: number,
  flagHeight: number,
  woodMat: THREE.MeshStandardMaterial,
  flagMat: THREE.MeshStandardMaterial
): THREE.Mesh {
  // Pole
  const poleGeom = new THREE.CylinderGeometry(0.025, 0.03, poleHeight, 6);
  const pole = new THREE.Mesh(poleGeom, woodMat);
  pole.position.set(x, y + poleHeight / 2, z);
  group.add(pole);

  // Top ornament
  const ornamentGeom = new THREE.SphereGeometry(0.05, 8, 6);
  const ornament = new THREE.Mesh(ornamentGeom, new THREE.MeshStandardMaterial({ color: 0xd4af37, metalness: 0.6, roughness: 0.3 }));
  ornament.position.set(x, y + poleHeight + 0.03, z);
  group.add(ornament);

  // Flag
  const flagGeom = new THREE.PlaneGeometry(flagWidth, flagHeight);
  const flag = new THREE.Mesh(flagGeom, flagMat);
  flag.position.set(x + flagWidth / 2 + 0.02, y + poleHeight - flagHeight / 2 - 0.05, z);
  flag.name = 'flag';
  group.add(flag);

  return flag;
}

/**
 * Create traditional curved roof
 */
export function createCurvedRoof(
  group: THREE.Group,
  width: number,
  depth: number,
  height: number,
  baseY: number,
  roofMat: THREE.MeshStandardMaterial,
  edgeMat: THREE.MeshStandardMaterial
): void {
  // Roof base
  const roofBaseGeom = new THREE.BoxGeometry(width, 0.15, depth);
  const roofBase = new THREE.Mesh(roofBaseGeom, roofMat);
  roofBase.position.y = baseY;
  roofBase.castShadow = true;
  group.add(roofBase);

  // Curved roof using cone
  const roofConeGeom = new THREE.ConeGeometry(Math.max(width, depth) / 2 * 0.9, height, 4);
  const roofCone = new THREE.Mesh(roofConeGeom, roofMat);
  roofCone.position.y = baseY + height / 2 + 0.05;
  roofCone.rotation.y = Math.PI / 4;
  roofCone.castShadow = true;
  group.add(roofCone);

  // Ridge decoration
  const ridgeGeom = new THREE.BoxGeometry(0.1, 0.15, depth * 0.7);
  const ridge = new THREE.Mesh(ridgeGeom, edgeMat);
  ridge.position.y = baseY + height + 0.05;
  group.add(ridge);

  // Eaves
  const eaveGeom = new THREE.BoxGeometry(width + 0.2, 0.06, depth + 0.2);
  const eave = new THREE.Mesh(eaveGeom, edgeMat);
  eave.position.y = baseY - 0.05;
  group.add(eave);

  // Corner decorations
  const cornerGeom = new THREE.BoxGeometry(0.15, 0.08, 0.3);
  const halfW = width / 2 + 0.05;
  const halfD = depth / 2 + 0.05;
  const corners = [
    { x: halfW, z: halfD, ry: -0.3 },
    { x: -halfW, z: halfD, ry: 0.3 },
    { x: halfW, z: -halfD, ry: 0.3 },
    { x: -halfW, z: -halfD, ry: -0.3 },
  ];
  for (const c of corners) {
    const corner = new THREE.Mesh(cornerGeom, edgeMat);
    corner.position.set(c.x, baseY - 0.02, c.z);
    corner.rotation.y = c.ry;
    corner.rotation.z = 0.2;
    group.add(corner);
  }
}
